import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { VxIconComponent } from '@ui/icon/icon.component';
import { VxIconName } from '@ui/icons';
import { CreatePageLayout, createPageVariants } from './create-page.variants';

/**
 * Shell of every resource-creation page: a back link on top, then the intro
 * (entity tile, title, subtitle and a short list of help links) and the form.
 * `layout="split"` (default) puts them side by side; `layout="stacked"` puts
 * the intro above the form for narrow hosts such as the settings shell.
 *
 * ```html
 * <app-create-page
 *   [title]="'pages.createMonitor.title' | transloco"
 *   [subtitle]="'pages.createMonitor.subtitle' | transloco"
 *   icon="pulse"
 *   [backLabel]="'pages.createMonitor.back' | transloco"
 *   [backLink]="['/monitors']">
 *   <a createPageAside href="…">How do I set up a monitor?</a>
 *   <form>…</form>
 * </app-create-page>
 * ```
 *
 * The page owns nothing but layout: the consumer projects its own form card
 * as default content and any number of help links into `createPageAside`.
 * All text arrives already translated, like `app-page-header`.
 */
@Component({
  selector: 'app-create-page',
  templateUrl: './create-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, VxIconComponent],
  host: { class: 'block font-brand' },
})
export class CreatePageComponent {
  /** Already-translated page title. */
  readonly title = input.required<string>();

  /** One line under the title, already translated. */
  readonly subtitle = input<string>();

  /** Entity tile above the title — the resource's icon. */
  readonly icon = input.required<VxIconName>();

  /** Already-translated label of the back link. */
  readonly backLabel = input.required<string>();

  /** Router target of the back link, usually the resource's list page. */
  readonly backLink = input.required<string | readonly unknown[]>();

  /** Intro beside the form (`split`) or above it (`stacked`). */
  readonly layout = input<CreatePageLayout>('split');

  // tv() slots are functions — resolve them to strings before binding
  protected readonly classes = computed(() => {
    const slots = createPageVariants({ layout: this.layout() });
    return {
      body: slots.body(),
      intro: slots.intro(),
      header: slots.header(),
      aside: slots.aside(),
      content: slots.content(),
    };
  });
}
